import ContractService from "@/modules/contracts/ContractService";
import cloneDeep from "lodash/cloneDeep";
import i18n from "@/shared/i18n";
import ContractSearchModel from "@/modules/contracts/ContractSearch.model";
import _ from "lodash";
import {store} from "@/plugins/vuex";
import {CONTRACTS_TABLE_NAME} from "@/modules/constants";

let getDefaultTableData = (differentFields = []) => {
  return {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new ContractSearchModel(),
    extraFilters: {},
    expand: 'contract_files',
    toggleColumns: {
      fields: [...getDefaultTableFields(), ...differentFields],
      requiredFields: ['filename'],
      defaultFields: [
        'filename',
        'sent_at',
        'sent_data',
        'created_at',
        'actions',
      ],
    }
  }
};

let getDefaultTableFields = () => {
  return [
    {
      key: 'filename',
      label: i18n.t('File Name'),
      sortable: true,
      isVisible: true,
    },
    {
      key: 'status',
      label: i18n.t('Status'),
      sortable: true,
      isVisible: true,
    },
    {
      key: 'sent_at',
      label: i18n.t('Sent At / Sent By'),
      sortable: true,
      isVisible: true,
      expand: 'sent_by'
    },
    {
      key: 'created_at',
      label: i18n.t('Created at / Created by'),
      sortable: true,
      isVisible: true,
      expand: 'created_by'
    },
    {
      key: 'comment',
      label: i18n.t('Comment'),
      sortable: true,
      isVisible: false,
      expand: ''
    },
    {key: 'actions', label: i18n.t('Actions'), sortable: false, isVisible: true,},
    {key: 'signature_platform', label: i18n.t('Signature Platform'), sortable: true, isVisible: false,},
    {key: 'type', label: i18n.t('Type'), sortable: true, isVisible: false,},
    {key: 'recipients', label: i18n.t('Recipients'), sortable: false, isVisible: false, expand: 'recipients'},
  ]
}

export default {
  namespaced: true,
  state: {
    tableSource: null,
    tableData: {
      customer: getDefaultTableData(),
      receipt: getDefaultTableData(),
    },
    viewModal: {
      show: false,
      contractFile: null
    },
    contractSendModal: {
      show: false,
      source: null,
      object: null,
      loading: false,
    },
    contractFilesPreviewModal: {
      show: false,
      source: null,
      customerUuid: null,
      files: null,
    },
    contractModal: {
      show: false,
      object: null,
      source: null,
      files: null,
    },
    contractLabelMap: {
      collection_note: i18n.t('Collection Note'),
      receipt_contract: i18n.t('Consignment'),
      pre_receipt_contract: i18n.t('Pre-Consignment'),
      return_note_contract: i18n.t('Return Note'),
      transfer_note_contract: i18n.t('Transfer Note'),
    }
  },
  actions: {
    resetTableDataPaginationPage({state}) {
      state.tableData[state.tableSource].pagination.page = 1;
    },
    updateSearchModel({commit, state}, searchModel) {
      commit('updateSearchModel', new ContractSearchModel({
        ...state.tableData[state.tableSource].searchModel,
        ...searchModel
      }));
    },
    async resetTable({state, dispatch}) {
      const extraFilters = state.tableData[state.tableSource].extraFilters;
      const tmpToggleColumns = _.cloneDeep(state.tableData[state.tableSource].toggleColumns);
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData[state.tableSource].pagination.limit;
      state.tableData[state.tableSource] = defaultTableData;
      state.tableData[state.tableSource].toggleColumns = tmpToggleColumns;
      state.tableData[state.tableSource].extraFilters = extraFilters;
      dispatch('getTableData');
    },
    async getTableData({commit, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      commit('updateTableLoading', true);
      const response = await ContractService.getContracts(params);
      commit('updateTableLoading', false);

      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }

      return response;
    },
    async getTableParams({state}, payload = null) {
      let data;
      let currentTableData = state.tableData[state.tableSource];
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: currentTableData,
        tableName: `${CONTRACTS_TABLE_NAME}.${state.tableSource}`
      })

      if (payload) {
        data = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        data = {
          ...currentTableData.pagination,
          search: currentTableData.search,
          ordering: ordering,
        }
      }

      if (currentTableData.searchModel) {
        const searchModelData = currentTableData.searchModel.toJSON();
        data = {
          ...data,
          ...searchModelData
        }
      }

      if (currentTableData.extraFilters) {
        data = {...data, ...currentTableData.extraFilters};
      }

      const defaultExpand = currentTableData.expand
      const expand = currentTableData.toggleColumns.fields
        .filter(c => currentTableData.toggleColumns.defaultFields.includes(c.key) && c.expand && !defaultExpand.includes(c.expand))
        .map(c => c.expand)
        .join(',')

      data.expand = `${expand},${defaultExpand}`;

      return data;
    },
    async updateTableSource({state}, {extraFilters, source}) {
      state.tableSource = source;
      state.tableData[source].extraFilters = extraFilters;
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    },


    async createContracts({state}, {formData, onUploadProgress}) {
      return ContractService.createContracts(formData, onUploadProgress);
    },
    async deleteContract({state}, uuid) {
      return ContractService.deleteContract(uuid);
    },
    async showContractSendModal({state}, {contract, source}) {
      state.contractSendModal.show = true
      state.contractSendModal.source = source
      state.contractSendModal.object = cloneDeep(contract)
    },
    async hideContractSendModal({state}) {
      state.contractSendModal.show = false
      state.contractSendModal.object = null
      state.contractSendModal.source = null
    },
    async toggleSendModalLoading({state}, val) {
      state.contractSendModal.loading = val
    },
    async showContractFilesPreviewModal({state}, {files, source, customerUuid}) {
      state.contractFilesPreviewModal.show = true
      state.contractFilesPreviewModal.source = source
      state.contractFilesPreviewModal.customerUuid = customerUuid
      state.contractFilesPreviewModal.files = cloneDeep(files)
    },
    async hideContractFilesPreviewModal({state}) {
      state.contractFilesPreviewModal.show = false
      state.contractFilesPreviewModal.files = null
      state.contractFilesPreviewModal.source = null
      state.contractFilesPreviewModal.customerUuid = null
    },
    async showContractModal({state}, {source, object}) {
      state.contractModal.show = true
      state.contractModal.source = source
      state.contractModal.object = _.cloneDeep(object)
    },
    async hideContractModal({state}) {
      state.contractModal.show = false
      state.contractModal.source = null
      state.contractModal.object = null
    },
  },
  mutations: {
    updateTableData: (state, payload) => state.tableData[state.tableSource] = {...state.tableData[state.tableSource], ...payload},
    updateTableLoading: (state, payload) => state.tableData[state.tableSource].loading = payload,
    updateSearchModel: (state, searchModel) => state.tableData[state.tableSource].searchModel = searchModel,
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData[state.tableSource].toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData[state.tableSource].toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData[state.tableSource].toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData[state.tableSource].toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
  }
}
